import { useCallback, useMemo } from 'react'

import { OnboardingResponseStatus } from 'clickNgo/configs/service/onboarding'
import {
  ContactFormContent,
  IContact,
  UNIQUE_ROLES,
  useHPRoleCheckDuplicates,
} from 'clickNgo/entities/contact'
import { validateContact } from 'clickNgo/entities/contact/api'
import { IPartner } from 'clickNgo/entities/partner/models'
import { getJSendErrorMessages } from 'clickNgo/helpers/api'
import { useGetFormDataField } from 'cng-common/features/form'
import Logger from 'core/utils/Logger'

import { ContactCreatorFormKeys, CreateContactFormContent } from '../../../../configs'
import { IContactFormKeys, TDuplicateHPRole } from '../../../../models'
import { getUpdateValue } from '../helpers/getUpdateValue'

interface IParams {
  contact: IContact
  partner: IPartner
  formKeys: IContactFormKeys
}

export const useHPRoleChange = ({ contact, partner, formKeys }: IParams) => {
  const [hpRoles] = useGetFormDataField<string[]>(
    formKeys.hpRoles,
    ContactFormContent.fields.hpRoles.field
  )
  const [duplicateHPRoles, updateDuplicateHPRoles] = useGetFormDataField<TDuplicateHPRole[]>(
    formKeys.settings,
    CreateContactFormContent.fields.duplicateHPRoles.field
  )

  const [, setErrorMessages] = useGetFormDataField<string[]>(
    ContactCreatorFormKeys.Settings,
    CreateContactFormContent.fields.errorMessages.field
  )

  const { checkHPRoleDuplicates } = useHPRoleCheckDuplicates({
    contact,
  })

  const hasDuplicateHPRoles = useMemo(() => Boolean(duplicateHPRoles?.length), [duplicateHPRoles])

  const handleOnHPRolesChange = useCallback(
    async (newHPRoles: string[]) => {
      const { isAddAction, updatedValue } = getUpdateValue(hpRoles, newHPRoles)

      if (!updatedValue) {
        return
      }

      const isUniqueRole = UNIQUE_ROLES.includes(updatedValue)

      if (isUniqueRole) {
        if (isAddAction) {
          try {
            const contactWithDuplicates = await checkHPRoleDuplicates(updatedValue)

            if (contactWithDuplicates?.length) {
              updateDuplicateHPRoles([
                ...duplicateHPRoles,
                { role: updatedValue, contacts: contactWithDuplicates },
              ])
            }
          } catch (error) {
            Logger.error(error)
          }
        } else {
          updateDuplicateHPRoles(
            duplicateHPRoles.filter((duplicate) => duplicate.role !== updatedValue)
          )
        }
      }

      const validateAsyncResult = await validateContact({
        contactId: contact.contactId,
        partnerId: contact.partnerId,
        country: partner.country,
        hpRoles,
      })

      if (validateAsyncResult.status === OnboardingResponseStatus.Fail) {
        setErrorMessages(getJSendErrorMessages(validateAsyncResult))
        return {
          hasError: true,
        }
      }

      if (validateAsyncResult.status === OnboardingResponseStatus.Error) {
        setErrorMessages([validateAsyncResult.message])
        return {
          hasError: true,
        }
      }

      return
    },
    [
      checkHPRoleDuplicates,
      contact.contactId,
      contact.partnerId,
      duplicateHPRoles,
      hpRoles,
      partner.country,
      setErrorMessages,
      updateDuplicateHPRoles,
    ]
  )

  return { handleOnHPRolesChange, hasDuplicateHPRoles }
}
